import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { SpinReward } from '../types';
import { getSpinRewards, getFreeSpinRewards } from '../services/api';

interface SpinRewardsContextType {
  paidRewards: SpinReward[];
  freeRewards: SpinReward[];
  isLoading: boolean;
  error: string | null;
  refreshRewards: () => Promise<void>;
}

const SpinRewardsContext = createContext<SpinRewardsContextType | undefined>(undefined);

export const useSpinRewards = () => {
  const context = useContext(SpinRewardsContext);
  if (context === undefined) {
    throw new Error('useSpinRewards must be used within a SpinRewardsProvider');
  }
  return context;
};

interface SpinRewardsProviderProps {
  children: ReactNode;
}

export const SpinRewardsProvider: React.FC<SpinRewardsProviderProps> = ({ children }) => {
  const [paidRewards, setPaidRewards] = useState<SpinReward[]>([]);
  const [freeRewards, setFreeRewards] = useState<SpinReward[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRewardsData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load both paid and free rewards in parallel
      const [paidRewardsData, freeRewardsData] = await Promise.all([
        getSpinRewards(),
        getFreeSpinRewards()
      ]);
      
      setPaidRewards(paidRewardsData);
      setFreeRewards(freeRewardsData);
    } catch (err) {
      console.error('Error loading rewards data:', err);
      setError('Failed to load rewards data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshRewards = async () => {
    await loadRewardsData();
  };

  useEffect(() => {
    loadRewardsData();
  }, []);

  const value = {
    paidRewards,
    freeRewards,
    isLoading,
    error,
    refreshRewards
  };

  return <SpinRewardsContext.Provider value={value}>{children}</SpinRewardsContext.Provider>;
};
